import { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Download, FileCheck2, AlertTriangle, Copy, PackageOpen, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

function Stat({ label, value, color, icon: Icon }) {
  return (
    <div className="glass rounded-xl px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono">{label}</span>
        {Icon && <Icon className="w-4 h-4" style={{ color }} />}
      </div>
      <div className="font-mono text-2xl font-medium tnum mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

export default function ImportWorkbook() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  if (user && !["coordinator", "manager", "admin"].includes(user.role)) return <Navigate to="/" replace />;

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "glh_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const runPreview = async (f) => {
    setBusy(true); setDone(null); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    runPreview(f);
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/commit", fd);
      setDone(data);
      setPreview(null);
      toast.success(`Imported ${data.inserted} referrals`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-5 max-w-5xl" data-testid="import-page">
      <div>
        <h1 className="font-head font-black text-3xl tracking-tight">Import Workbook</h1>
        <p className="text-sm text-muted-foreground font-mono mt-1">One-time migration · de-dupes · coerces dates · parks un-coded Not-Completeds for review</p>
      </div>

      <div className="glass rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={downloadTemplate} className="gap-2 border-white/15" data-testid="download-template-btn">
            <Download className="w-4 h-4" /> Download CSV template
          </Button>
          <label className="inline-flex items-center gap-2 cursor-pointer rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-4 py-2 text-sm transition-colors" data-testid="upload-label">
            <Upload className="w-4 h-4" /> {file ? "Choose another file" : "Upload CSV / XLSX"}
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="file-input" />
          </label>
          {file && <span className="text-xs font-mono text-muted-foreground">{file.name}</span>}
          {busy && <span className="text-xs font-mono text-cyan-400 animate-pulse">processing…</span>}
        </div>
        <p className="text-xs text-muted-foreground font-mono mt-3">Required columns: patient_name, mrn, source_line, receiving_line, refer_date. Optional: referral_source, location, receiving_contact, status, close_date, recheck_date, owner, reason_code, completion_type, note.</p>
      </div>

      {preview && (
        <div className="space-y-4" data-testid="import-preview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Ready to import" value={preview.clean_count} color="#34D399" icon={FileCheck2} />
            <Stat label="Parked (review)" value={preview.parked_count} color="#F59E0B" icon={PackageOpen} />
            <Stat label="Duplicates skipped" value={preview.duplicate_count} color="#64748B" icon={Copy} />
            <Stat label="Errors skipped" value={preview.error_count} color="#EF4444" icon={AlertTriangle} />
          </div>

          {(preview.auto_mapped?.length > 0 || preview.unmapped_columns?.length > 0) && (
            <div className="glass rounded-xl p-4" data-testid="column-mapping">
              <div className="text-[11px] uppercase tracking-wide font-mono mb-2 text-cyan-300">Auto-mapped columns</div>
              {preview.auto_mapped?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {preview.auto_mapped.map((m, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/30 bg-cyan-500/5 px-2 py-1 text-xs font-mono">
                      <span className="text-slate-300">{m.from}</span>
                      <span className="text-cyan-400">→</span>
                      <span className="text-cyan-200">{m.to}</span>
                    </span>
                  ))}
                </div>
              ) : <p className="text-xs font-mono text-muted-foreground">Headers already matched the standard names.</p>}
              {preview.unmapped_columns?.length > 0 && (
                <p className="text-xs font-mono text-amber-400/80 mt-2">Ignored (unrecognized): {preview.unmapped_columns.join(", ")}</p>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <PreviewList title="Ready to import (sample)" color="#34D399" rows={preview.clean_sample.map((r) => `${r.initials} · ${r.lane} · ${r.status} · ${r.refer_date}`)} />
            <PreviewList title="Parked — un-coded Not-Completed" color="#F59E0B" rows={preview.parked_sample.map((r) => `${r.initials} · ${r.lane} · refer ${r.refer_date}`)} empty="None parked." />
          </div>

          {preview.errors.length > 0 && (
            <PreviewList title="Errors (rows skipped)" color="#EF4444" rows={preview.errors.map((e) => `Row ${e.row}: ${e.reason}`)} />
          )}

          <div className="flex justify-end gap-2">
            <Button onClick={commit} disabled={busy || (preview.clean_count + preview.parked_count === 0)} className="gap-2 bg-violet-500 hover:bg-violet-400 text-white font-semibold" data-testid="commit-import-btn">
              <CheckCircle2 className="w-4 h-4" /> Import {preview.clean_count + preview.parked_count} records
            </Button>
          </div>
        </div>
      )}

      {done && (
        <div className="glass glass-cyan rounded-xl p-5" data-testid="import-done">
          <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-5 h-5 text-emerald-400" /><span className="font-head font-bold">Import complete</span></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-sm">
            <div>Inserted <span className="text-emerald-400 tnum">{done.inserted}</span></div>
            <div>Parked <span className="text-amber-400 tnum">{done.parked}</span></div>
            <div>Duplicates <span className="text-slate-400 tnum">{done.skipped_duplicates}</span></div>
            <div>Errors <span className="text-red-400 tnum">{done.errors}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewList({ title, color, rows, empty }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="text-[11px] uppercase tracking-wide font-mono mb-2" style={{ color }}>{title}</div>
      {rows.length === 0 && <p className="text-xs text-muted-foreground font-mono">{empty || "—"}</p>}
      <div className="space-y-1">
        {rows.map((r, i) => <div key={i} className="text-xs font-mono text-slate-300 truncate">{r}</div>)}
      </div>
    </div>
  );
}
